import { TrajectoryXyzModel } from 'src/app/shared/models/trajectory-xyz.model';

export interface CameraEye {
  x: number;
  y: number;
  z: number;
}

export interface Trajectory3dBuildResult {
  plotlyData: unknown[];
  layout: Record<string, unknown>;
  minRange: number;
  maxRange: number;
}

export function buildTrajectory3dPlot(params: {
  plotData: TrajectoryXyzModel;
  isShowingProjections: boolean;
  isDarkMode: boolean;
  longLengths: string;
}): Trajectory3dBuildResult {
  const { plotData, isShowingProjections, isDarkMode, longLengths } = params;

  const minRanges = [Math.min(...plotData.x), Math.min(...plotData.y), Math.min(...plotData.z)];
  const minRange = Math.min(...minRanges);

  const maxRanges = [Math.max(...plotData.x), Math.max(...plotData.y), Math.max(...plotData.z)];
  const maxRange = Math.max(...maxRanges);

  const layout = create3dLayout({
    isDarkMode,
    longLengths,
    minRange,
    maxRange
  });
  layout.showlegend = false;

  const norView = { ...plotData, x: [] as number[] };
  const estView = { ...plotData, y: [] as number[] };
  const planView = { ...plotData, z: [] as number[] };

  const xyRange = Math.max(...[Math.abs(minRange), Math.abs(maxRange)]);
  const zMax = Math.max(...plotData.z);

  for (let i = 0; i < plotData.x.length; i++) {
    norView.x.push(-xyRange);
    estView.y.push(-xyRange);
    planView.z.push(zMax);
  }

  const baseData = [
    {
      name: '3D Line',
      line: { shape: 'spline', smoothing: 1.3, width: 10 },
      mode: 'lines',
      type: 'scatter3d',
      ...plotData,
    }
  ];

  if (!isShowingProjections) {
    return {
      plotlyData: baseData,
      layout,
      minRange,
      maxRange
    };
  }

  const lineColor = isDarkMode ? '#FFF' : '#000';
  const line = { shape: 'spline', smoothing: 1.3, width: 1, color: lineColor, dash: 'dash' };

  const views = [
    { name: 'Plan', ...planView },
    { name: 'Northing', ...estView },
    { name: 'Easting', ...norView }
  ];

  const projectionData = views.map(view => ({
    name: view.name,
    line,
    mode: 'lines',
    type: 'scatter3d',
    ...view
  }));

  return {
    plotlyData: [...baseData, ...projectionData],
    layout,
    minRange,
    maxRange
  };
}

export function create3dLayout(params: {
  isDarkMode: boolean;
  longLengths: string;
  minRange: number;
  maxRange: number;
}): Record<string, unknown> {
  const { isDarkMode, longLengths, minRange, maxRange } = params;
  const plotBackgroundColor = isDarkMode ? '#1C1C1C' : '#fff';
  const fontColor = isDarkMode ? 'white' : 'black';
  return {
    autoexpand: true,
    autosize: true,
    margin: { t: 20, r: 10, l: 10, b: 10 },
    offset: 0,
    type: 'scatter3d',
    mode: 'lines',
    hovermode: 'closest',
    plot_bgcolor: plotBackgroundColor,
    paper_bgcolor: plotBackgroundColor,
    xaxis: createLayoutAxis(`Northing (${longLengths})`, plotBackgroundColor),
    yaxis: createLayoutAxis(`Easting (${longLengths})`, plotBackgroundColor),
    zaxis: createLayoutAxis(`TVD (${longLengths})`, plotBackgroundColor),
    legend: {
      font: {
        color: fontColor
      },
      orientation: 'h'
    },
    scene: {
      aspectmode: 'cube',
      camera: {
        projection: 'orthographic',
        center: {
          x: 0, y: 0, z: -0.25
        },
        eye: {
          x: 1.35, y: 1.35, z: 1.25
        },
        up: {
          x: 0, y: 0, z: 0.5
        }
      },
      zaxis: createSceneAxisObj(`TVD (${longLengths})`, true, fontColor, minRange, maxRange),
      xaxis: createSceneAxisObj(`Northing (${longLengths})`, false, fontColor, minRange, maxRange),
      yaxis: createSceneAxisObj(`Easting (${longLengths})`, false, fontColor, minRange, maxRange)
    }
  };
}

export function rotateCameraEye(currentEye: CameraEye, angle: number): CameraEye {
  const rtz = xyz2rtz(currentEye);
  rtz.t += angle;
  return rtz2xyz(rtz);
}

function createLayoutAxis(title: string, lineColor: string): Record<string, unknown> {
  return {
    automargin: true,
    linecolor: lineColor,
    mirror: true,
    title
  };
}

function createSceneAxisObj(
  title: string,
  flipAxis: boolean,
  fontColor: string,
  minRange: number,
  maxRange: number
): Record<string, unknown> {
  const xyRange = Math.max(...[Math.abs(minRange), Math.abs(maxRange)]);
  return {
    range: flipAxis ? [maxRange, 0] : [-xyRange, xyRange],
    linewidth: 2,
    gridcolor: '#454545',
    linecolor: 'gray',
    title,
    tickfont: {
      color: fontColor
    },
    titlefont: {
      color: fontColor
    },
    hoverformat: '.2f'
  };
}

function xyz2rtz(xyz: CameraEye): { r: number; t: number; z: number } {
  return {
    r: Math.sqrt(xyz.x * xyz.x + xyz.y * xyz.y),
    t: Math.atan2(xyz.y, xyz.x),
    z: xyz.z
  };
}

function rtz2xyz(rtz: { r: number; t: number; z: number }): CameraEye {
  return {
    x: rtz.r * Math.cos(rtz.t),
    y: rtz.r * Math.sin(rtz.t),
    z: rtz.z
  };
}
